import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  ANY_SPORT_LINEUPS_ARENA_FIXTURE,
  ANY_SPORT_LINEUPS_PRO,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { ModeIcon } from '#src/components/ModeIcon/index.tsx';
import { useCurrentLineupMode } from '#src/hooks/useCurrentLineupMode.ts';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';
import { LineupMode, Mode } from '#src/lib/gameMode.ts';

type Props = {
  fixture?: string;
};

const Switcher = styled(Horizontal).attrs({ gap: 1, center: true })`
  justify-content: center;
`;

const messages = defineMessages({
  pro: {
    defaultMessage: 'Pro',
  },
  arena: {
    defaultMessage: 'Arena',
  },
});

type PillConfig = {
  mode: LineupMode;
  label: string;
  icon: ReactNode;
  accentRgb: string;
  to?: string;
};

export const LineupsModeSwitcher = ({ fixture = 'ongoing' }: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const { updateBoardView } = useLastVisitedBoardView();
  const currentMode = useCurrentLineupMode();

  const pills: PillConfig[] = [
    {
      mode: LineupMode.PRO,
      label: formatMessage(messages.pro),
      icon: <ModeIcon mode={Mode.PRO} size={2.5} />,
      accentRgb: 'var(--c-rgb-inventory-orange)',
      to: generateSportContextPath(ANY_SPORT_LINEUPS_PRO),
    },
    {
      mode: LineupMode.ARENA,
      label: formatMessage(messages.arena),
      icon: <ModeIcon mode={Mode.ARENA_IN_SEASON} size={2.5} />,
      accentRgb: 'var(--c-rgb-unique)',
      to: generateSportContextPath(ANY_SPORT_LINEUPS_ARENA_FIXTURE, {
        params: { fixture },
      }),
    },
  ];

  const onSelect = (mode: LineupMode) => {
    if (mode === LineupMode.PRO) updateBoardView({ lastVisitedMode: Mode.PRO });
    if (mode === LineupMode.ARENA)
      updateBoardView({ lastVisitedMode: Mode.ARENA_IN_SEASON });
  };

  return (
    <Switcher>
      {pills.map(({ mode, label, icon, accentRgb, to }) => {
        const selected = mode === currentMode;
        const transitionId = `lineups-mode-${mode.toLowerCase()}`;
        return (
          <PillButton
            key={mode}
            size="medium"
            to={to}
            disabled={!to}
            selected={selected}
            startIcon={icon}
            aria-label={label}
            accentRgb={accentRgb}
            viewTransitionName={transitionId}
            onClick={() => onSelect(mode)}
          >
            {selected ? label : null}
          </PillButton>
        );
      })}
    </Switcher>
  );
};
